import React, { useCallback, useEffect, useState } from "react";
import {
  Building2, Loader2, Mail, MailCheck, Percent, Save, Send, Settings as Cog,
  FileText, Hash, ShieldCheck, History, Package, Truck,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/pages/crm/LeadForm";
import { ProductsPanel } from "./settings/ProductsPanel";
import { SuppliersPanel } from "./settings/SuppliersPanel";
import { PermissionMatrix } from "./settings/PermissionMatrix";

const DOC_KEYS = [
  ["quotation", "Quotation"], ["proforma", "Proforma Invoice"],
  ["invoice", "Invoice"], ["purchase_order", "Purchase Order"],
];
const TEMPLATE_KEYS = [...DOC_KEYS, ["payment_reminder", "Payment Reminder"], ["followup_reminder", "Follow-up Reminder"]];
const NUM_KEYS = [
  ["lead", "Leads"], ["customer", "Customers"], ["quotation", "Quotations"], ["proforma", "Proforma Invoices"],
  ["invoice", "Invoices"], ["purchase_order", "Purchase Orders"], ["payment", "Payments"], ["stock", "Stock Transactions"],
];

const VARIABLES = ["{customer_name}", "{company_name}", "{staff_name}", "{document_number}", "{document_date}",
  "{quotation_number}", "{pi_number}", "{invoice_number}", "{po_number}", "{invoice_total}",
  "{total_amount}", "{due_date}", "{payment_status}", "{product_name}"];

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState([]);
  const [meta, setMeta] = useState({ modules: [], actions: [] });
  const [logs, setLogs] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [testTo, setTestTo] = useState("");

  const load = useCallback(async () => {
    const [st, r, m] = await Promise.all([api.get("/settings"), api.get("/roles"), api.get("/roles/meta")]);
    setS(st.data); setRoles(r.data); setMeta(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadLogs = () => api.get("/activity").then((r) => setLogs(r.data)).catch(() => {});
  const loadOutbox = () => api.get("/email-outbox").then((r) => setOutbox(r.data)).catch(() => {});

  const save = async (patch, label = "Settings saved") => {
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", patch);
      setS(data);
      toast.success(label);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const upd = (section, key, value) =>
    setS((prev) => ({ ...prev, [section]: { ...prev[section], [key]: value } }));

  if (!s) {
    return <div className="space-y-4"><Skeleton className="h-20 rounded-xl" /><Skeleton className="h-96 rounded-xl" /></div>;
  }

  const SaveBtn = ({ onClick, testId }) => (
    <Button onClick={onClick} disabled={saving} data-testid={testId}>
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageHeader icon={Cog} title="Admin Settings" subtitle="Company profile, product presets, suppliers, documents, email and permissions." />

      <Tabs defaultValue="company">
        <div className="scroll-slim -mx-1 overflow-x-auto px-1 pb-1">
          <TabsList className="inline-flex w-auto">
            <TabsTrigger value="company" data-testid="tab-company"><Building2 className="mr-1.5 h-3.5 w-3.5" />Company</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products"><Package className="mr-1.5 h-3.5 w-3.5" />Products</TabsTrigger>
            <TabsTrigger value="suppliers" data-testid="tab-suppliers"><Truck className="mr-1.5 h-3.5 w-3.5" />Suppliers</TabsTrigger>
            <TabsTrigger value="pricing"><Percent className="mr-1.5 h-3.5 w-3.5" />Pricing &amp; Tax</TabsTrigger>
            <TabsTrigger value="terms"><FileText className="mr-1.5 h-3.5 w-3.5" />Terms</TabsTrigger>
            <TabsTrigger value="email" data-testid="tab-email"><Mail className="mr-1.5 h-3.5 w-3.5" />Email</TabsTrigger>
            <TabsTrigger value="templates"><MailCheck className="mr-1.5 h-3.5 w-3.5" />Templates</TabsTrigger>
            <TabsTrigger value="numbering"><Hash className="mr-1.5 h-3.5 w-3.5" />Numbering</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles"><ShieldCheck className="mr-1.5 h-3.5 w-3.5" />Roles</TabsTrigger>
            <TabsTrigger value="activity" onClick={loadLogs}><History className="mr-1.5 h-3.5 w-3.5" />Activity log</TabsTrigger>
            <TabsTrigger value="outbox" onClick={loadOutbox} data-testid="tab-outbox"><Send className="mr-1.5 h-3.5 w-3.5" />Email outbox</TabsTrigger>
          </TabsList>
        </div>

        {/* COMPANY */}
        <TabsContent value="company" className="mt-5 space-y-5">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-display text-base">Company details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Company name"><Input value={s.company?.name || ""} data-testid="company-name" onChange={(e) => upd("company", "name", e.target.value)} /></Field>
                <Field label="Tagline"><Input value={s.company?.tagline || ""} onChange={(e) => upd("company", "tagline", e.target.value)} /></Field>
                <Field label="Email"><Input value={s.company?.email || ""} onChange={(e) => upd("company", "email", e.target.value)} /></Field>
                <Field label="Phone"><Input value={s.company?.phone || ""} onChange={(e) => upd("company", "phone", e.target.value)} /></Field>
                <Field label="Website"><Input value={s.company?.website || ""} onChange={(e) => upd("company", "website", e.target.value)} /></Field>
                <Field label="GSTIN"><Input value={s.company?.gstin || ""} onChange={(e) => upd("company", "gstin", e.target.value)} /></Field>
              </div>
              <Field label="Registered address">
                <Textarea rows={2} value={s.company?.address || ""} onChange={(e) => upd("company", "address", e.target.value)} />
              </Field>
              <SaveBtn onClick={() => save({ company: s.company })} testId="save-company" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="font-display text-base">Bank details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Bank name"><Input value={s.bank?.bank_name || ""} onChange={(e) => upd("bank", "bank_name", e.target.value)} /></Field>
                <Field label="Account name"><Input value={s.bank?.account_name || ""} onChange={(e) => upd("bank", "account_name", e.target.value)} /></Field>
                <Field label="Account number"><Input value={s.bank?.account_number || ""} onChange={(e) => upd("bank", "account_number", e.target.value)} /></Field>
                <Field label="IFSC code"><Input value={s.bank?.ifsc || ""} onChange={(e) => upd("bank", "ifsc", e.target.value)} /></Field>
                <Field label="Branch"><Input value={s.bank?.branch || ""} onChange={(e) => upd("bank", "branch", e.target.value)} /></Field>
                <Field label="UPI ID"><Input value={s.bank?.upi || ""} onChange={(e) => upd("bank", "upi", e.target.value)} /></Field>
              </div>
              <SaveBtn onClick={() => save({ bank: s.bank })} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="mt-5">
          <ProductsPanel markup={s.pricing?.markup_percent ?? 40} />
        </TabsContent>

        <TabsContent value="suppliers" className="mt-5">
          <SuppliersPanel />
        </TabsContent>

        {/* PRICING */}
        <TabsContent value="pricing" className="mt-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-base">Default selling price rule</CardTitle>
              <p className="text-sm text-muted-foreground">Selling price = purchase cost + markup. Staff can still edit the price on a document if permitted.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Default markup percentage">
                  <Input type="number" min="0" value={s.pricing?.markup_percent ?? 40} data-testid="markup"
                    onChange={(e) => upd("pricing", "markup_percent", Number(e.target.value))} />
                </Field>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-3 text-sm">
                    <Switch checked={!!s.pricing?.auto_price} onCheckedChange={(v) => upd("pricing", "auto_price", v)} />
                    Apply automatically to new products
                  </label>
                </div>
              </div>
              <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
                Example: purchase cost <b className="num">100</b> becomes selling price{" "}
                <b className="num text-primary">{Math.round(100 * (1 + (s.pricing?.markup_percent ?? 40) / 100))}</b>
              </div>
              <Field label="Available tax rates (comma separated)">
                <Input value={(s.tax_rates || []).join(", ")}
                  onChange={(e) => setS({ ...s, tax_rates: e.target.value.split(",").map((t) => Number(t.trim())).filter((n) => !isNaN(n)) })} />
              </Field>
              <SaveBtn onClick={() => save({ pricing: s.pricing, tax_rates: s.tax_rates })} testId="save-pricing" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TERMS */}
        <TabsContent value="terms" className="mt-5 space-y-5">
          {DOC_KEYS.map(([k, label]) => (
            <Card key={k}>
              <CardHeader className="pb-3"><CardTitle className="font-display text-base">{label} terms &amp; conditions</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Textarea rows={5} value={s.terms?.[k] || ""} onChange={(e) => upd("terms", k, e.target.value)} />
              </CardContent>
            </Card>
          ))}
          <SaveBtn onClick={() => save({ terms: s.terms }, "Terms updated")} testId="save-terms" />
        </TabsContent>

        {/* EMAIL */}
        <TabsContent value="email" className="mt-5 space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-base">SMTP configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                Until these are filled in, every email is stored safely in the Email Outbox instead of being sent.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="SMTP host" hint="e.g. smtp.gmail.com">
                  <Input value={s.email?.smtp_host || ""} data-testid="smtp-host" onChange={(e) => upd("email", "smtp_host", e.target.value)} />
                </Field>
                <Field label="SMTP port" hint="587 for TLS, 465 for SSL">
                  <Input type="number" value={s.email?.smtp_port || 587} onChange={(e) => upd("email", "smtp_port", Number(e.target.value))} />
                </Field>
                <Field label="SMTP username"><Input value={s.email?.smtp_user || ""} onChange={(e) => upd("email", "smtp_user", e.target.value)} /></Field>
                <Field label="SMTP password / app password">
                  <Input type="password" value={s.email?.smtp_password || ""} onChange={(e) => upd("email", "smtp_password", e.target.value)} />
                </Field>
                <Field label="Default sender name"><Input value={s.email?.from_name || ""} onChange={(e) => upd("email", "from_name", e.target.value)} /></Field>
                <Field label="From email"><Input value={s.email?.from_email || ""} onChange={(e) => upd("email", "from_email", e.target.value)} /></Field>
                <Field label="Reply-to email"><Input value={s.email?.reply_to || ""} onChange={(e) => upd("email", "reply_to", e.target.value)} /></Field>
              </div>
              <div className="flex flex-wrap gap-2">
                <SaveBtn onClick={() => save({ email: s.email }, "Email configuration saved")} testId="save-email" />
                <div className="flex gap-2">
                  <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="send test to..." className="w-56" />
                  <Button variant="outline" onClick={async () => {
                    const { data } = await api.post("/email-test", { to: testTo });
                    if (data.status === "sent") toast.success("Test email sent");
                    else if (data.status === "outbox") toast.warning("SMTP not configured. Saved to Email Outbox.");
                    else toast.error(data.error);
                  }} disabled={!testTo}><Send className="h-4 w-4" /> Test</Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-base">Default CC recipients</CardTitle>
              <p className="text-sm text-muted-foreground">Separate multiple emails with a comma. Users can still add or remove recipients before sending.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Accountant email"><Input value={s.email?.accountant_email || ""} onChange={(e) => upd("email", "accountant_email", e.target.value)} /></Field>
                <Field label="Director email"><Input value={s.email?.director_email || ""} onChange={(e) => upd("email", "director_email", e.target.value)} /></Field>
                <Field label="Manager email"><Input value={s.email?.manager_email || ""} onChange={(e) => upd("email", "manager_email", e.target.value)} /></Field>
              </div>
              {DOC_KEYS.map(([k, label]) => (
                <Field key={k} label={`${label} CC list`}>
                  <Input
                    value={((s.email?.cc_rules || {})[k] || []).join(", ")}
                    onChange={(e) => upd("email", "cc_rules", {
                      ...(s.email?.cc_rules || {}),
                      [k]: e.target.value.split(",").map((x) => x.trim()).filter(Boolean),
                    })}
                  />
                </Field>
              ))}
              <SaveBtn onClick={() => save({ email: s.email }, "CC rules saved")} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-base">Directors notify list</CardTitle>
              <p className="text-sm text-muted-foreground">
                These addresses are copied whenever a demo or training is rescheduled, so the directors
                always know about a date change. Separate multiple emails with a comma.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Field label="Director email addresses">
                <Input data-testid="director-emails"
                  value={((s.notifications?.director_emails) || []).join(", ")}
                  onChange={(e) => upd("notifications", "director_emails",
                    e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
                  placeholder="director1@megatronixvision.com, director2@megatronixvision.com" />
              </Field>
              <SaveBtn onClick={() => save({ notifications: s.notifications }, "Directors notify list saved")}
                testId="save-directors" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEMPLATES */}
        <TabsContent value="templates" className="mt-5 space-y-5">
          <Card>
            <CardContent className="py-4">
              <p className="text-sm font-medium">Available variables</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {VARIABLES.map((v) => (
                  <code key={v} className="rounded bg-primary-soft px-2 py-0.5 text-[11px] text-primary">{v}</code>
                ))}
              </div>
            </CardContent>
          </Card>
          {TEMPLATE_KEYS.map(([k, label]) => (
            <Card key={k}>
              <CardHeader className="pb-3"><CardTitle className="font-display text-base">{label} email</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <Field label="Subject">
                  <Input value={s.templates?.[k]?.subject || ""}
                    onChange={(e) => upd("templates", k, { ...(s.templates?.[k] || {}), subject: e.target.value })} />
                </Field>
                <Field label="Message body">
                  <Textarea rows={6} value={s.templates?.[k]?.body || ""}
                    onChange={(e) => upd("templates", k, { ...(s.templates?.[k] || {}), body: e.target.value })} />
                </Field>
              </CardContent>
            </Card>
          ))}
          <SaveBtn onClick={() => save({ templates: s.templates }, "Templates saved")} testId="save-templates" />
        </TabsContent>

        {/* NUMBERING */}
        <TabsContent value="numbering" className="mt-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-base">Document numbering</CardTitle>
              <p className="text-sm text-muted-foreground">Set the prefix and how many digits each number should have.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                {NUM_KEYS.map(([k, label]) => {
                  const n = (s.numbering || {})[k] || { prefix: "", padding: 4 };
                  return (
                    <Field key={k} label={label} hint={`Preview: ${n.prefix}-${"1".padStart(n.padding || 4, "0")}`}>
                      <div className="flex gap-2">
                        <Input value={n.prefix} onChange={(e) => upd("numbering", k, { ...n, prefix: e.target.value.toUpperCase() })} />
                        <Select value={String(n.padding || 4)} onValueChange={(v) => upd("numbering", k, { ...n, padding: Number(v) })}>
                          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>{[3, 4, 5, 6].map((p) => <SelectItem key={p} value={String(p)}>{p} digits</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </Field>
                  );
                })}
              </div>
              <SaveBtn onClick={() => save({ numbering: s.numbering }, "Numbering saved")} testId="save-numbering" />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ROLES */}
        <TabsContent value="roles" className="mt-5 space-y-5">
          <RolesPanel roles={roles} meta={meta} onReload={load} />
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-base">Activity log &amp; audit trail</CardTitle>
              <p className="text-sm text-muted-foreground">Who did what, when, and what changed.</p>
            </CardHeader>
            <CardContent>
              <DataTable
                rows={logs}
                columns={[
                  { key: "created_at", label: "When", render: (r) => <span className="text-xs text-muted-foreground">{fdatetime(r.created_at)}</span> },
                  { key: "user", label: "User", render: (r) => <div><p className="text-sm font-medium">{r.user_name}</p><p className="text-xs text-muted-foreground">{r.user_email} . {r.user_role}</p></div> },
                  { key: "module", label: "Module", render: (r) => <StatusBadge status={r.module} tone="neutral" dot={false} />, hideMobile: true },
                  { key: "record", label: "Action", render: (r) => <div><p className="text-sm">{r.record}</p>{r.ref && <p className="num text-xs text-muted-foreground">{r.ref}</p>}</div> },
                  {
                    key: "changes", label: "Changes", hideMobile: true,
                    render: (r) => (r.changes || []).length === 0 ? <span className="text-xs text-muted-foreground">\u2014</span> : (
                      <div className="space-y-0.5">
                        {(r.changes || []).slice(0, 3).map((c, i) => (
                          <p key={i} className="text-[11px]">
                            <span className="font-medium">{c.field}</span>:{" "}
                            <span className="text-destructive line-through">{String(c.from ?? "empty").slice(0, 24)}</span>{" \u2192 "}
                            <span className="text-success">{String(c.to ?? "empty").slice(0, 24)}</span>
                          </p>
                        ))}
                      </div>
                    ),
                  },
                ]}
                empty={<EmptyState icon={History} title="No activity recorded yet" />}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* OUTBOX */}
        <TabsContent value="outbox" className="mt-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-base">Email outbox</CardTitle>
              <p className="text-sm text-muted-foreground">
                Every email the system produced. Messages marked <b>outbox</b> were not delivered because SMTP is not configured yet.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {outbox.length === 0 ? (
                <EmptyState icon={Mail} title="No emails yet" description="Verification codes and document emails will appear here." />
              ) : outbox.map((m) => (
                <details key={m.id} className="rounded-xl border border-border bg-card p-4">
                  <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                    <StatusBadge status={m.status} tone={m.status === "sent" ? "success" : m.status === "failed" ? "danger" : "warning"} />
                    <span className="flex-1 truncate text-sm font-medium">{m.subject}</span>
                    <span className="text-xs text-muted-foreground">{fdatetime(m.created_at)}</span>
                  </summary>
                  <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                    <p><b className="text-foreground">To:</b> {m.to.join(", ")}</p>
                    {m.cc?.length > 0 && <p><b className="text-foreground">CC:</b> {m.cc.join(", ")}</p>}
                    {m.error && <p className="text-warning">{m.error}</p>}
                  </div>
                  <div className="mt-3 whitespace-pre-line rounded-lg bg-muted/50 p-3 text-sm"
                    dangerouslySetInnerHTML={{ __html: m.body }} />
                </details>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const RolesPanel = ({ roles, meta, onReload }) => {
  const [active, setActive] = useState(roles[0]?.key || "admin");
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [newRole, setNewRole] = useState("");

  useEffect(() => {
    const r = roles.find((x) => x.key === active);
    setDraft(r?.permissions || {});
  }, [active, roles]);

  const role = roles.find((x) => x.key === active);

  const saveRole = async () => {
    setBusy(true);
    try {
      await api.patch(`/roles/${active}`, { name: role.name, description: role.description, permissions: draft });
      toast.success("Permissions updated");
      onReload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const create = async () => {
    if (!newRole.trim()) return;
    try {
      await api.post("/roles", { name: newRole.trim(), permissions: { dashboard: ["view"] } });
      toast.success("Role created");
      setNewRole(""); onReload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base">User roles &amp; permissions</CardTitle>
          <p className="text-sm text-muted-foreground">Permissions are enforced on the server, not just hidden in the menu.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {roles.map((r) => (
              <button key={r.key} onClick={() => setActive(r.key)} data-testid={`role-${r.key}`}
                className={`rounded-lg border px-3 py-2 text-sm transition-colors ${
                  active === r.key ? "border-primary bg-primary-soft text-primary" : "border-border hover:border-primary/40"}`}>
                {r.name}
              </button>
            ))}
          </div>
          {role && (
            <>
              <p className="text-sm text-muted-foreground">{role.description}</p>
              <PermissionMatrix modules={meta.modules} actions={meta.actions} value={draft} onChange={setDraft} />
              <Button onClick={saveRole} disabled={busy || role.key === "admin"} data-testid="save-role">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {role.key === "admin" ? "Admin always has full access" : "Save permissions"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-display text-base">Create a new role</CardTitle></CardHeader>
        <CardContent>
          <div className="flex max-w-md gap-2">
            <Input value={newRole} onChange={(e) => setNewRole(e.target.value)} placeholder="e.g. Accounts Executive" data-testid="new-role-name" />
            <Button variant="outline" onClick={create}>Create role</Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
